import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AtendimentoType,
  formatDateToPtBr,
  IconPaths,
  initialQueryState,
  OptionsHelper,
  Permission,
  useDebounce,
  validateDateRange,
  VinculoType,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { AtendimentosMedicoFilters, useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  Button,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useAuth } from '../../../../../auth'
import { isVinculoMedico } from 'app/modules/auth/core/vinculos'
import { useT } from '_metronic/i18n/Metronici18n'

/** Filtros de select (IOption): o chip mostra o rótulo da opção e o × limpa o campo. */
type SelectFilterKey = Exclude<
  keyof AtendimentosMedicoFilters,
  'paciente' | 'protocolo' | 'range' | 'inicio' | 'fim'
>

type Props = {
  /** Estado selecionado nos cards acima da tabela (entra na busca, mas não vira chip). */
  estado: number
}

export const AtendimentoMedicoListSearchComponent = ({ estado }: Props) => {
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest()
  const { response, refetch, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const [searchParams] = useSearchParams()
  const tipoParam = searchParams.get('tipo')
  const total = response?.totalElements ?? 0

  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  useEffect(() => {
    const tipo = optionsTiposAtendimento.find((option) => option.value === tipoParam)
    if (tipo) setFilter('tipoAtendimento', tipo)
  }, [tipoParam, optionsTiposAtendimento, setFilter])

  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.inicio, filters.fim),
    [filters.inicio, filters.fim]
  )

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(() => {
    const { error } = validateDateRange(debouncedFilters.inicio, debouncedFilters.fim)
    const { range, inicio, fim } = debouncedFilters

    return [
      debouncedFilters.retorno ? `retorno:${debouncedFilters.retorno.value}` : '',
      debouncedFilters.empresa ? `paciente.empresa:${debouncedFilters.empresa.value}` : '',
      debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
      estado ? `estado:${estado}` : '',
      debouncedFilters.especialidade ? `especialidade:${debouncedFilters.especialidade.value}` : '',
      debouncedFilters.protocolo.trim() ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
      debouncedFilters.paciente.trim()
        ? `paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*`
        : '',
      debouncedFilters.unidade ? `unidade.id:${debouncedFilters.unidade.value}` : '',
      debouncedFilters.vinculo ? `responsavel.vinculo:${debouncedFilters.vinculo.value}` : '',
      !error && range && inicio && fim ? `${range.value}>${inicio},${range.value}<${fim}` : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters, estado])

  useEffect(() => {
    updateState({ search, ...initialQueryState })
  }, [search, updateState])

  // Unidade depende da instituição: sem instituição, não há unidade.
  useEffect(() => {
    if (!filters.empresa && filters.unidade) setFilter('unidade', undefined)
  }, [filters.empresa, filters.unidade, setFilter])

  const grupo = currentUser?.grupo?.nome
  const vinculos = currentUser?.vinculos ?? []
  const isAdmin = grupo === Permission.Admin || grupo === Permission.SuperAdmin
  const hasNonMedicoCargo = vinculos.some((v) => !isVinculoMedico(v))
  const isMedicoResponsavel = vinculos.some((v) => isVinculoMedico(v) && v.responsavel)
  const showAllEspecialidades = isAdmin || hasNonMedicoCargo || isMedicoResponsavel

  const empresaParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Período começado e incompleto: destaca o que falta.
  const periodoIniciado = !!(filters.range || filters.inicio || filters.fim)
  const periodoCompleto = !!(filters.range && filters.inicio && filters.fim)
  const faltando = (campo: unknown) => periodoIniciado && !periodoCompleto && !campo
  const erroInicio = faltando(filters.inicio)
    ? 'Selecione uma data inicial'
    : dateError.includes('inicial')
    ? dateError
    : undefined
  const erroFim = faltando(filters.fim)
    ? 'Selecione uma data final'
    : dateError && !dateError.includes('inicial')
    ? dateError
    : undefined

  const limparPeriodo = () => {
    setFilter('range', undefined)
    setFilter('inicio', '')
    setFilter('fim', '')
  }

  const optionChip = (key: SelectFilterKey, label: string): ActiveFilter | undefined => {
    const option = filters[key]
    return option
      ? { key, label, value: option.label, onRemove: () => setFilter(key, undefined) }
      : undefined
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o período sai inteiro).
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: 'Paciente',
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: 'Protocolo',
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    optionChip('empresa', t('empresa.label')),
    optionChip('unidade', 'Unidade/Setor'),
    optionChip('especialidade', 'Especialidade'),
    optionChip('retorno', 'Retorno'),
    optionChip('tipoAtendimento', 'Tipo'),
    optionChip('vinculo', 'Vínculo'),
    periodoCompleto && {
      key: 'periodo',
      label: filters.range!.label,
      value: `${formatDateToPtBr(filters.inicio)} a ${formatDateToPtBr(filters.fim)}`,
      onRemove: limparPeriodo,
    },
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  const moreFiltersActiveCount = [
    filters.especialidade,
    filters.retorno,
    filters.tipoAtendimento,
    filters.vinculo,
    periodoIniciado,
  ].filter(Boolean).length

  return (
    <div className='card-header border-0 pt-6'>
      <TableFilters
        layout='stacked'
        actions={
          <Button
            variant='secondary-outline'
            size='btn-sm'
            icon={IconPaths.Refresh}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {isLoading ? 'Atualizando…' : 'Atualizar'}
          </Button>
        }
        moreFiltersActiveCount={moreFiltersActiveCount}
        moreFilters={
          <>
            <TableFilter label='Especialidade'>
              <CustomSelect
                placeholder='Selecione uma especialidade'
                onChange={(especialidade?: IOption<number>) =>
                  setFilter('especialidade', especialidade ?? undefined)
                }
                fetchOptions={
                  showAllEspecialidades
                    ? OptionsHelper.getOptionsEspecialidades
                    : OptionsHelper.getOptionsEspecialidadesByProfissional
                }
                value={filters.especialidade}
              />
            </TableFilter>
            <TableFilter label='Consulta de retorno?'>
              <CustomSelect
                placeholder='Selecione'
                options={OptionsHelper.booleanOptions}
                onChange={(retorno?: IOption<boolean>) =>
                  setFilter('retorno', retorno ?? undefined)
                }
                value={filters.retorno}
              />
            </TableFilter>
            <TableFilter label='Tipo de atendimento'>
              <CustomSelect
                placeholder='Selecione um tipo'
                options={optionsTiposAtendimento}
                onChange={(tipo?: IOption<AtendimentoType>) =>
                  setFilter('tipoAtendimento', tipo ?? undefined)
                }
                value={filters.tipoAtendimento}
              />
            </TableFilter>
            <TableFilter label='Vínculo'>
              <CustomSelect
                placeholder='Selecione um vínculo'
                fetchOptions={OptionsHelper.getOptionsTiposVinculos}
                onChange={(vinculo?: IOption<VinculoType>) =>
                  setFilter('vinculo', vinculo ?? undefined)
                }
                value={filters.vinculo}
              />
            </TableFilter>
            <TableFilter
              label='Consultas entre'
              error={faltando(filters.range) ? 'Selecione uma opção' : undefined}
            >
              <CustomSelect
                placeholder='Selecione uma opção'
                options={OptionsHelper.consultaDatasOptions}
                onChange={(range?: IOption<string>) => setFilter('range', range ?? undefined)}
                value={filters.range}
              />
            </TableFilter>
            <TableFilter label='Data inicial' error={erroInicio}>
              <DateInput
                value={filters.inicio}
                onChange={(e) => setFilter('inicio', e.target.value)}
                minDate={dateLimits.startMin}
                maxDate={dateLimits.startMax}
                isInvalid={Boolean(erroInicio)}
              />
            </TableFilter>
            <TableFilter label='Data final' error={erroFim}>
              <DateInput
                value={filters.fim}
                onChange={(e) => setFilter('fim', e.target.value)}
                minDate={dateLimits.endMin}
                maxDate={dateLimits.endMax}
                isInvalid={Boolean(erroFim)}
              />
            </TableFilter>
          </>
        }
        footer={
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={clearFilters}
            summary={
              isLoading
                ? 'Buscando…'
                : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
            }
          />
        }
      >
        <TableFilter label='Paciente'>
          <Input
            placeholder='Digite o nome do paciente'
            value={filters.paciente}
            onChange={(e) => setFilter('paciente', e.target.value)}
          />
        </TableFilter>
        <TableFilter label='Protocolo'>
          <Input
            placeholder='Digite o protocolo'
            value={filters.protocolo}
            onChange={(e) => setFilter('protocolo', e.target.value)}
          />
        </TableFilter>
        <TableFilter label={t('empresa.label')}>
          <CustomSelect
            entity='empresa'
            onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            value={filters.empresa}
            autoSelectSingle
          />
        </TableFilter>
        <TableFilter label='Unidade/Setor'>
          <CustomSelect
            placeholder={
              filters.empresa
                ? 'Selecione uma unidade'
                : `Selecione a ${t('empresa.label.lowercase')}`
            }
            onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
            fetchOptions={OptionsHelper.getOptionsUnidades}
            fetchOptionsParams={empresaParams}
            value={filters.unidade}
            isDisabled={!filters.empresa}
          />
        </TableFilter>
      </TableFilters>
    </div>
  )
}
